import axios from 'axios';

export const getBaseURL = () => {
  // Forçar uso da API local quando rodando no localhost
  if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
    return 'http://localhost:8001/api';
  }

  if (process.env.NODE_ENV === 'production') {
    return process.env.REACT_APP_API_URL || 'https://admin-api.futezap.com.br/api';
  }
  return process.env.REACT_APP_API_URL || 'http://localhost:8001/api';
};

const api = axios.create({
  baseURL: getBaseURL(),
});

// Time Futezap ativo da AREA DO JOGADOR (switcher do CommunityLayout).
// Separado de 'active_customer_id', que eh o time ativo do PAINEL.
export const PLAYER_ACTIVE_TEAM_KEY = 'player_active_customer_id';

// /time/{slug} eh PUBLICO (perfil do time). So consideramos area logada
// as rotas do TeamOwnerLayout com paths conhecidos.
const TEAM_AUTH_PATHS = ['dashboard', 'setup', 'editar', 'upgrade', 'mapa',
  'procurar', 'convites', 'feed', 'amistosos', 'meus-amistosos', 'jogos', 'elenco', 'escalacao',
  'convocacao'];

const isTeamOwnerAuthPath = (path) =>
  TEAM_AUTH_PATHS.some(p => path === `/time/${p}` || path.startsWith(`/time/${p}/`));

// Telas logadas do app da Comunidade (CommunityLayout / TeamOwnerLayout).
// Essas telas so renderizam com community_token valido, entao o token certo
// nelas eh SEMPRE o do jogador — independente do host. Antes a escolha era
// so pelo host: em admin.futezap.com.br/player/* (push, LP de avaliacao) ou
// localhost, um token do painel vencido no LS ia nas chamadas, voltava 401
// e o jogador era deslogado logo apos o login.
const isCommunityAppPage = (path) =>
  (path.startsWith('/player/') && path !== '/player/login' && path !== '/player/register')
  || isTeamOwnerAuthPath(path);

api.interceptors.request.use((config) => {
  // Verificar se é uma rota de comunidade/player.
  // IMPORTANTE: /community/teams/* é do ORGANIZADOR (usa admin token, nao
  // community_token) — apesar do prefixo. Mesmo pra invites/team, ratings
  // do admin, etc. Se cair no 'community route' aqui, o interceptor manda
  // o token errado e o backend rejeita como 403.
  const url = config.url;
  const isCommunityHostReq = typeof window !== 'undefined' && window.location.hostname.startsWith('comunidade.');
  // NOTA: /community/teams/:id/roster eh do jogador DONO do time comunidade
  // (authenticatePlayer), nao do admin. Excecao explicita pra nao mandar
  // admin token la — se nao, quando o dono jogador abre /time/elenco no
  // comunidade.futezap.com.br, vai sem community_token e da 401.
  // NOTA 2 (2026-08-29): a exceção acima era pontual pro roster mas o
  // mesmo problema afeta news/report/qualquer rota do dono em
  // comunidade.futezap.com.br. Fix definitivo: no host comunidade, NUNCA
  // tratar como admin-only — sempre usa community_token. Isso cobre
  // /community/teams/:id/news, /community/news/:id/report e outras rotas
  // do dono do time que caíam no regex admin-only por acidente.
  // NOTA 3: o mesmo vale pras telas logadas do app da Comunidade em
  // qualquer host (ver isCommunityAppPage).
  const preferCommunityToken = isCommunityHostReq ||
    (typeof window !== 'undefined' && isCommunityAppPage(window.location.pathname));
  const isRosterRoute = /\/community\/teams\/\d+\/roster/.test(url);
  const isAdminOnlyCommunityRoute = !isRosterRoute && !preferCommunityToken && /\/community\/teams\/|\/community\/invites\/team|\/community\/ratings\/pending|\/community\/ratings\/rate-player|\/community\/invites\/invite-player/.test(url);
  const isCommunityRoute = !isAdminOnlyCommunityRoute && (
                          url.includes('/community/') ||
                          url.includes('/player-teams/') ||
                          url.includes('/game-ratings/') ||
                          url.includes('/affiliates/player/') ||
                          url.includes('/player/referrals') ||
                          url.includes('/push/player'));

  // Escolha de token pra rota comunidade:
  // - host comunidade.futezap.com.br ou tela logada do app da Comunidade:
  //   prioriza community_token (o jogador/dono ta logado como player)
  // - demais (painel em admin.futezap.com.br): prioriza admin token (evita
  //   usar community_token stale de sessao antiga; o backend aceita admin
  //   em endpoints comunidade via authenticatePlayer/authenticateCommunity)
  const communityToken = localStorage.getItem('community_token');
  const adminToken = localStorage.getItem('token');
  const token = isCommunityRoute
    ? (preferCommunityToken
        ? (communityToken || adminToken)
        : (adminToken || communityToken))
    : adminToken;

  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  // Guarda qual token foi usado — o interceptor de resposta precisa saber
  // se um 401 derrubou a sessao do jogador ou so o token do painel.
  config._authSource = !token ? null : (token === communityToken ? 'community' : 'admin');

  // Injeta o time ativo em TODAS as chamadas (comunidade e admin).
  // Backend prefere esse header sobre o customer_id do JWT — cobre casos
  // em que o token nao foi renovado apos switch de time (multi-tab, cache,
  // hard-refresh sem re-login).
  // Painel e area do jogador guardam o time ativo em chaves separadas: no
  // mesmo host (admin.futezap / localhost) o switcher do jogador sobrescrevia
  // o time do painel e o organizador levava 403 "Sem acesso a esse time".
  const onPlayerPage = typeof window !== 'undefined' && window.location.pathname.startsWith('/player/');
  const activeCustomerId = localStorage.getItem(onPlayerPage ? PLAYER_ACTIVE_TEAM_KEY : 'active_customer_id');
  if (activeCustomerId && !config.headers['x-active-customer-id']) {
    config.headers['x-active-customer-id'] = activeCustomerId;
    if (!onPlayerPage) config._panelActiveTeam = activeCustomerId;
  }
  return config;
});

api.interceptors.response.use(
  (response) => response,
  (error) => {
    // Autocorrecao: time ativo do painel invalido (ex.: gravado pela versao
    // antiga do switcher do jogador). Sem isso, organizador com 1 time so
    // ficava com o menu Comunidade quebrado (nao tem switcher pra trocar e o
    // logout nao limpa a chave). Sem o header, o backend volta a usar o
    // customer_id do JWT — por isso nao entra em loop.
    const sentPanelTeam = error.config?._panelActiveTeam;
    if (error.response?.status === 403
        && error.response?.data?.message === 'Sem acesso a esse time'
        && sentPanelTeam
        && localStorage.getItem('active_customer_id') === sentPanelTeam) {
      localStorage.removeItem('active_customer_id');
      window.location.reload();
    }

    if (error.response?.status === 401) {
      const url = error.config?.url || '';
      const currentPath = window.location.pathname;
      const currentHost = window.location.hostname;
      const isCommunityHost = currentHost.startsWith('comunidade.');
      const isPlayerRoute = currentPath.startsWith('/player/');
      const isTimeRoute = isTeamOwnerAuthPath(currentPath);
      // Bug real reportado (2026-08-24): organizador com community_token
      // expirado no LS logava no admin, request pra /community/* usava esse
      // community_token expirado -> 401 -> interceptor removia o token
      // admin valido (!) e mandava pro /login em loop. Fix: se o 401 veio
      // de uma chamada que usou community_token (URL comunidade + LS tinha
      // community_token), so limpa community_* e nao redireciona.
      const usedCommunityToken = !!localStorage.getItem('community_token') && (
        url.includes('/community/') || url.includes('/player-teams/') ||
        url.includes('/game-ratings/') || url.includes('/affiliates/player/') ||
        url.includes('/player/referrals') || url.includes('/push/player')
      );
      if ((isPlayerRoute || isTimeRoute) && error.config?._authSource === 'admin') {
        // Tela de jogador/dono, mas a chamada foi com token do painel (ex.:
        // header forcado na mao) e ele que caiu. Sessao do jogador continua
        // valida — limpa so o token do painel, sem redirect.
        localStorage.removeItem('token');
        localStorage.removeItem('user');
      } else if (isPlayerRoute) {
        localStorage.removeItem('community_token');
        localStorage.removeItem('community_user');
        window.location.href = '/player/login';
      } else if (isTimeRoute) {
        localStorage.removeItem('community_token');
        localStorage.removeItem('community_user');
        window.location.href = '/player/login?next=' + encodeURIComponent(currentPath);
      } else if (usedCommunityToken && !isCommunityHost) {
        // Admin.futezap com community_token expirado — invalida so o cache
        // do community_token; admin token continua valido, sem redirect.
        localStorage.removeItem('community_token');
        localStorage.removeItem('community_user');
      } else if (!isCommunityHost) {
        // Admin (admin.futezap.com.br) — de fato o token admin caiu.
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        window.location.href = '/login';
      }
      // Nos demais casos (rota publica de comunidade), silencia o 401 — quem
      // chamou lida com o erro (usualmente ignora, pois a chamada era opcional).
    }
    return Promise.reject(error);
  }
);

export default api;